import React from "react";
import { CpuChipIcon } from "@heroicons/react/24/solid";
import EditOverlay from "./EditOverlay";
import { GPUInstanceType } from "types/InstanceTypes";
import { Button } from "components/Graphs/Button";
import { motion } from "framer-motion";
import EstimatedStartTimeBadge from "./EstimatedStartTimeBadge";
import RackCardLabel from "./RackCardLabel";
import classNames from "classnames";
import { CloudIcon } from "lucide-react";
import CloudDisplay from "components/Graphs/CloudDisplay";
import DiagonalRackPattern from "./DiagonalRackPattern";
import PowerButton from "./PowerButton";
import CannotStopWarning from "./CannotStopWarning";
import GpuCountLabel from "./GpuCountLabel";
import { formatMemoryForDisplay } from "../../utils/index";
import { isDgxSpark } from "utils/instanceTypes";
import DGXSparkModel from "./DGXSparkModel";
import { displayPlatformType } from "components/Environment/utils";
import { FreeTierPriceWithTooltip } from "components/FreeTier";
import { getInstanceTypeArchitectures } from "utils/architecture";
import { formatFixedStorage } from "./fixedStorage";
import FixedStorageDescription from "./FixedStorageDescription";

interface RackCardProps {
  setInstanceType: (instanceType: GPUInstanceType) => void;
  instanceType: GPUInstanceType;
  gpuCount: number;
  vramSize: string;
  ramSize: string;
  cpuCount: number;
  cloudProvider: string;
  cloud: string | undefined;
  price: string;
  isFreeTier?: boolean;
  flexiblePortControlOnly?: boolean;
  flexibleStorageOnly?: boolean;
  selected?: boolean;
}

const RackVent = ({
  orientation = "horizontal",
  hidden = false,
  className,
}: {
  orientation?: "horizontal" | "vertical";
  hidden?: boolean;
  className?: string;
}) => (
  <div
    className={classNames(
      "bg-primaryBg rounded-md",
      orientation === "horizontal" ? "w-2 h-6" : "w-6 h-2",
      hidden && "invisible",
      className,
    )}
  />
);

const RackCard: React.FC<RackCardProps> = ({
  instanceType,
  setInstanceType,
  gpuCount,
  vramSize,
  ramSize,
  cpuCount,
  cloudProvider,
  cloud,
  price,
  isFreeTier = false,
  flexiblePortControlOnly = false,
  flexibleStorageOnly = false,
  selected = false,
}) => {
  const fixedStorage = formatFixedStorage(instanceType.supported_storage);

  const warningPortsVisible = !flexibleStorageOnly || !flexiblePortControlOnly;
  const isSpark = isDgxSpark(instanceType.type);
  const architectures = getInstanceTypeArchitectures(instanceType);

  const cardStyles = isSpark
    ? {
        container: {
          background: `
          radial-gradient(circle at 80% 20%, rgba(166, 139, 91, 0.08) 0%, transparent 40%),
          linear-gradient(135deg, #000 0%, #1c1812 50%, #0f0e0c 100%)
        `,
          boxShadow: `0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)`,
        },
        border: selected
          ? "border-[#c5b277]/80 shadow-lg shadow-[#c5b277]/20"
          : "border-zinc-800 hover:shadow-xl",
        hoverOutline:
          "hover:outline-2 hover:[outline-color:rgba(197,178,119,0.5)]",
        text: "text-[#E6D5B3]",
        cloudText: "text-zinc-400",
        dotColor: "text-zinc-400",
      }
    : {
        container: {
          background: `
          repeating-linear-gradient(0deg, #1a1a1a 0px, #1a1a1a 1px, #2a2a2a 1px, #2a2a2a 2px),
          repeating-linear-gradient(90deg, #1a1a1a 0px, #1a1a1a 1px, #2a2a2a 1px, #2a2a2a 2px)
        `,
          boxShadow: `
          0 20px 40px rgba(0, 0, 0, 0.6),
          inset 0 0 20px rgba(0, 0, 0, 0.5),
          inset 0 1px 0 rgba(255, 255, 255, 0.1)
        `,
        },
        border: selected
          ? "border-zinc-800"
          : "border-zinc-800 hover:shadow-xl",
        hoverOutline: "hover:outline-2 hover:outline-highlight",
        text: "text-primary",
        cloudText: "text-secondary",
        dotColor: "text-primary",
      };

  const warningPorts = (
    <>
      {/* Only show storage label if not filtering for flexible storage */}
      {!flexibleStorageOnly && (
        <div className="flex gap-3">
          {instanceType.elastic_root_volume ? (
            <RackCardLabel
              showLight={!isSpark}
              key={`storage-badge-flexible`}
              color="green"
              style={isSpark ? "dark" : "light"}
              label={`Flexible storage`}
              tooltip="Configure any amount of storage when creating the instance"
            />
          ) : fixedStorage ? (
            <RackCardLabel
              showLight={!isSpark}
              key="storage-badge-fixed"
              color="orange"
              style={isSpark ? "dark" : "light"}
              label={<FixedStorageDescription description={fixedStorage} />}
              tooltip={`Storage is fixed at ${fixedStorage} by the cloud provider.`}
            />
          ) : null}
        </div>
      )}
      {/* Only show ports label if not filtering for flexible ports */}
      {!flexiblePortControlOnly && (
        <RackCardLabel
          showLight={!isSpark}
          label={
            instanceType.can_modify_firewall_rules
              ? "Flexible ports"
              : "Fixed ports"
          }
          color={instanceType.can_modify_firewall_rules ? "green" : "orange"}
          style={isSpark ? "dark" : "light"}
          tooltip={
            instanceType.can_modify_firewall_rules
              ? "Configure custom firewall rules and open/close ports anytime"
              : "Firewall rules and ports cannot be modified"
          }
        />
      )}
    </>
  );

  const startStopInfo = isSpark ? null : (
    <div className="flex flex-row justify-end gap-2 items-center">
      {instanceType.estimated_deploy_time && (
        <EstimatedStartTimeBadge
          estimatedStartTime={instanceType.estimated_deploy_time}
        />
      )}
      {instanceType.stoppable ? <PowerButton /> : <CannotStopWarning />}
    </div>
  );

  return (
    <Button
      variant="noshow"
      onClick={() => {
        setInstanceType(instanceType);
      }}
      className={classNames(
        "w-full hover:scale-[1.02] transition-transform duration-200 ease-out",
        cardStyles.hoverOutline,
      )}
    >
      <motion.div
        className={classNames(
          "group w-full p-4 rounded-md border-2 transition-shadow duration-200 relative overflow-hidden",
          cardStyles.border,
        )}
        key={instanceType.type}
        style={cardStyles.container}
      >
        {isSpark && (
          <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none flex items-center justify-start pl-[400px] xs:pl-[500px]">
            <div
              className="transform scale-[0.9] transform-origin-left translate-y-[18px] rotate-[10deg] opacity-15 mix-blend-overlay"
              style={{
                maskImage:
                  "linear-gradient(to right, black 50%, transparent 100%)",
                WebkitMaskImage:
                  "linear-gradient(to right, black 50%, transparent 100%)",
              }}
            >
              <DGXSparkModel width="600px" />
            </div>
          </div>
        )}
        <div className="flex flex-row h-full justify-between gap-4 relative z-10">
          <div className="flex flex-col items-center gap-2 md:justify-center">
            {gpuCount > 0 && (
              <div className="pt-0.5 flex-none">
                <GpuCountLabel count={`${gpuCount}x`} />
              </div>
            )}
            <div
              className={classNames(
                "flex flex-col items-center gap-2 justify-center flex-1 xl:hidden",
                !warningPortsVisible && "md:hidden",
              )}
            >
              <RackVent orientation="vertical" hidden={isSpark} />
              <RackVent orientation="vertical" hidden={isSpark} />
              <div className="flex flex-col gap-2 lg:hidden">
                <RackVent orientation="vertical" hidden={isSpark} />
                <RackVent orientation="vertical" hidden={isSpark} />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-3 justify-between flex-1 justify-items-stretch">
            <div className="flex flex-col">
              <div className="flex flex-row gap-2 items-center justify-between">
                <div className="text-[17px] text-primary opacity-80 flex flex-row gap-2 items-center">
                  <span className="text-primary flex items-center">
                    {selected &&
                      !isSpark &&
                      instanceType.supported_gpus?.[0]?.name && (
                        <span className="mr-1">
                          {instanceType.supported_gpus[0].name.replace(
                            "NVIDIA ",
                            "",
                          )}{" "}
                          •
                        </span>
                      )}
                    {isSpark && (
                      <span className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#ffe4b5] via-[#d4c095] to-[#a08a64] mr-2 filter drop-shadow-sm">
                        DGX Spark
                      </span>
                    )}
                    {vramSize
                      ? `${formatMemoryForDisplay(vramSize)} VRAM`
                      : `${formatMemoryForDisplay(ramSize)} RAM • ${cpuCount} vCPUs`}
                  </span>
                </div>
                <div className="flex flex-row gap-4 items-center">
                  <div className="hidden lg:flex flex-row gap-2 items-center justify-between">
                    <RackVent hidden={isSpark} />
                    <RackVent hidden={isSpark} />
                    <RackVent hidden={isSpark} />
                    <div className="flex xl:hidden gap-2">
                      <RackVent hidden={isSpark} />
                      <RackVent hidden={isSpark} />
                      <RackVent hidden={isSpark} />
                      <RackVent hidden={isSpark} />
                      <RackVent hidden={isSpark} />
                    </div>
                  </div>
                  <div className="flex flex-row gap-2 items-center">
                    {!isSpark && (
                      <div className="flex-row gap-2 items-center hidden sm:flex">
                        <CloudDisplay
                          icon={<CloudIcon className="h-4 w-4" />}
                          provider={displayPlatformType(cloudProvider)}
                          cloud={displayPlatformType(cloud || "")}
                          className={classNames(
                            "text-sm",
                            cardStyles.cloudText,
                          )}
                        />
                        {!!price && (
                          <span
                            className={classNames(
                              "opacity-25",
                              cardStyles.dotColor,
                            )}
                          >
                            ●
                          </span>
                        )}
                      </div>
                    )}
                    <div>
                      {price && (
                        <div
                          className={classNames(
                            "block text-[17px] font-semibold text-right self-end flex-none",
                            cardStyles.text,
                          )}
                        >
                          {isFreeTier ? (
                            <FreeTierPriceWithTooltip price={price} />
                          ) : (
                            <>${price}/hr</>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-row gap-2 justify-between flex-1 items-stretch">
              <div className="flex flex-col gap-2 justify-center">
                <div className="flex flex-col md:flex-row gap-1 md:gap-1.5 max-w-fit justify-center">
                  {architectures.length > 0 && (
                    <RackCardLabel
                      showLight={!isSpark}
                      label={architectures.join(" / ")}
                      icon={
                        <CpuChipIcon
                          className={classNames(
                            "h-4 w-4",
                            isSpark && "text-zinc-500",
                          )}
                        />
                      }
                      style={isSpark ? "dark" : "light"}
                    />
                  )}
                  {instanceType.supported_gpus?.[0]?.network_details && (
                    <RackCardLabel
                      showLight={!isSpark}
                      label={instanceType.supported_gpus[0].network_details.toUpperCase()}
                      icon={
                        <CpuChipIcon
                          className={classNames(
                            "h-4 w-4",
                            isSpark && "text-[#C4AF85]",
                          )}
                        />
                      }
                      style={isSpark ? "dark" : "light"}
                    />
                  )}
                  <RackCardLabel
                    showLight={!isSpark}
                    label={`${formatMemoryForDisplay(ramSize)} RAM x ${cpuCount} CPUS`}
                    icon={
                      <CpuChipIcon
                        className={classNames(
                          "h-4 w-4",
                          isSpark && "text-zinc-500",
                        )}
                      />
                    }
                    style={isSpark ? "dark" : "light"}
                  />
                </div>

                {/* Warning ports shown at the bottom of the specs on < 2xl screens */}
                <div className="flex flex-col 2xl:hidden gap-2 items-start">
                  {warningPorts}
                </div>
              </div>

              {/* Diagonal rack pattern shown between instance type specs and warnings */}
              <div
                className={classNames(
                  "flex flex-row items-stretch justify-center flex-1",
                  isSpark && "invisible",
                )}
              >
                <DiagonalRackPattern />
              </div>

              {/* Warning ports shown on the right-hand side of the card on larger screens */}
              {warningPortsVisible && (
                <div className="hidden 2xl:flex flex-row items-stretch justify-center flex-0 gap-1.5">
                  {warningPorts}
                </div>
              )}

              {/* Start/stop info shown on the right-hand side of the card on 2xl screens */}
              <div
                className={classNames(
                  "flex-0 hidden 2xl:flex",
                  !warningPortsVisible && "md:flex",
                )}
              >
                {startStopInfo}
              </div>
            </div>

            {/* Start/stop info shown at the bottom of the card on < 2xl screens */}
            <div
              className={classNames(
                "flex flex-row items-center justify-end 2xl:hidden",
                !warningPortsVisible && "md:hidden",
              )}
            >
              {startStopInfo}
            </div>
          </div>
        </div>
        {selected && <EditOverlay />}
      </motion.div>
    </Button>
  );
};

export default RackCard;
